import { useState } from "react";
import { useTranslation } from "react-i18next";
import DateRange from "@/model/DateRange";
import "@/components/BookingCalendar/BookingCalendar.scss";

interface BookingCalendarProps {
  /** Already-booked night ranges; each range's end is its check-out day and stays free. */
  bookedRanges: DateRange[];
  checkIn: Date | null;
  checkOut: Date | null;
  onChange: (checkIn: Date | null, checkOut: Date | null) => void;
  minNights?: number;
  today?: Date;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function nightsBetween(from: Date, to: Date): number {
  return Math.round(
    (startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000
  );
}

/**
 * Two-click stay picker: the first click picks the check-in day, the second the check-out day.
 * A night is unavailable when it falls inside a booked range; a booked range's first day can still
 * be picked as a check-out day (same-day turnover).
 */
function BookingCalendar({
  bookedRanges,
  checkIn,
  checkOut,
  onChange,
  minNights = 1,
  today: todayOverride,
}: BookingCalendarProps) {
  const { t, i18n } = useTranslation();
  const today = startOfDay(todayOverride ?? new Date());
  const [viewMonth, setViewMonth] = useState(() =>
    startOfMonth(checkIn ?? today)
  );

  const isNightBooked = (day: Date): boolean => {
    const dayStart = startOfDay(day);
    return bookedRanges.some(
      (range) => dayStart >= startOfDay(range.start) && dayStart < startOfDay(range.end)
    );
  };

  const isPastDay = (day: Date): boolean => startOfDay(day) < today;

  const nightsAreFree = (from: Date, to: Date): boolean => {
    for (let cursor = startOfDay(from); cursor < startOfDay(to); cursor = addDays(cursor, 1)) {
      if (isNightBooked(cursor)) {
        return false;
      }
    }
    return true;
  };

  const choosingCheckOut = checkIn !== null && checkOut === null;

  const canCheckInOn = (day: Date): boolean => !isPastDay(day) && !isNightBooked(day);

  const canCheckOutOn = (day: Date): boolean =>
    checkIn !== null &&
    day > startOfDay(checkIn) &&
    nightsBetween(checkIn, day) >= minNights &&
    nightsAreFree(checkIn, day);

  const isDayEnabled = (day: Date): boolean =>
    choosingCheckOut ? canCheckOutOn(day) || canCheckInOn(day) : canCheckInOn(day);

  const handleDayClick = (day: Date) => {
    if (choosingCheckOut && canCheckOutOn(day)) {
      onChange(checkIn, day);
      return;
    }
    if (canCheckInOn(day)) {
      onChange(day, null);
    }
  };

  const monthStart = startOfMonth(viewMonth);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const days: Date[] = Array.from({ length: 42 }, (_, index) =>
    addDays(gridStart, index)
  );

  const weekdayFormatter = new Intl.DateTimeFormat(i18n.language, {
    weekday: "short",
  });
  const monthFormatter = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
    year: "numeric",
  });
  const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
    weekdayFormatter.format(addDays(gridStart, index))
  );
  const dayFormatter = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="booking-calendar">
      <div className="booking-calendar__header">
        <button
          type="button"
          onClick={() => setViewMonth(addDays(monthStart, -1))}
          aria-label={t("calendar.previousMonth")}
        >
          ‹
        </button>
        <span>{monthFormatter.format(monthStart)}</span>
        <button
          type="button"
          onClick={() => setViewMonth(addDays(monthStart, 32))}
          aria-label={t("calendar.nextMonth")}
        >
          ›
        </button>
      </div>

      <div className="booking-calendar__weekdays">
        {weekdayLabels.map((label, index) => (
          <span key={index}>{label}</span>
        ))}
      </div>

      <div className="booking-calendar__grid">
        {days.map((day) => {
          const enabled = isDayEnabled(day);
          const booked = isNightBooked(day);
          const past = isPastDay(day);
          const outsideMonth = day.getMonth() !== monthStart.getMonth();
          const isEndpoint =
            (checkIn && isSameDay(day, checkIn)) ||
            (checkOut && isSameDay(day, checkOut));
          const inRange =
            checkIn && checkOut && day >= startOfDay(checkIn) && day <= startOfDay(checkOut);

          const classNames = [
            "booking-calendar__day",
            outsideMonth && "booking-calendar__day--outside",
            booked && !enabled && "booking-calendar__day--blocked",
            past && !booked && "booking-calendar__day--past",
            inRange && "booking-calendar__day--in-range",
            isEndpoint && "booking-calendar__day--endpoint",
            isSameDay(day, today) && "booking-calendar__day--today",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={day.toISOString()}
              type="button"
              className={classNames}
              disabled={!enabled}
              onClick={() => handleDayClick(day)}
              aria-label={dayFormatter.format(day)}
              aria-pressed={Boolean(isEndpoint)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <p className="booking-calendar__hint">
        {choosingCheckOut
          ? t("calendar.pickCheckOut", { count: minNights })
          : t("calendar.pickCheckIn")}
      </p>

      <div className="booking-calendar__legend">
        <span className="booking-calendar__legend-item">
          <span className="booking-calendar__swatch booking-calendar__swatch--blocked" />
          {t("calendar.legendBooked")}
        </span>
        <span className="booking-calendar__legend-item">
          <span className="booking-calendar__swatch booking-calendar__swatch--selected" />
          {t("calendar.legendSelected")}
        </span>
      </div>
    </div>
  );
}

export default BookingCalendar;
export type { BookingCalendarProps };
